import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { WS_URL } from "@/lib/api";

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
    const { user } = useAuth();
    const [statuses, setStatuses] = useState({}); // { app_id: { status, last_checked, response_time_ms } }
    const [connected, setConnected] = useState(false);
    const [discoveryEvent, setDiscoveryEvent] = useState(null);
    const wsRef = useRef(null);
    const reconnectRef = useRef(null);

    const connect = useCallback(() => {
        const token = localStorage.getItem("appdash_token");
        if (!token) return;
        try {
            const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
            wsRef.current = ws;

            ws.onopen = () => {
                setConnected(true);
            };
            ws.onmessage = (e) => {
                try {
                    const msg = JSON.parse(e.data);
                    if (msg.type === "snapshot") {
                        const next = {};
                        for (const a of msg.apps) {
                            next[a.app_id] = {
                                status: a.status,
                                last_checked: a.last_checked,
                                response_time_ms: a.response_time_ms,
                            };
                        }
                        setStatuses(next);
                    } else if (msg.type === "app_status") {
                        setStatuses((prev) => ({
                            ...prev,
                            [msg.app_id]: {
                                status: msg.status,
                                last_checked: msg.last_checked,
                                response_time_ms: msg.response_time_ms,
                            },
                        }));
                    } else if (msg.type === "discovery_complete") {
                        setDiscoveryEvent({ ...msg, ts: Date.now() });
                    }
                } catch (_) {
                    /* ignore */
                }
            };
            ws.onclose = () => {
                setConnected(false);
                wsRef.current = null;
                if (localStorage.getItem("appdash_token")) {
                    reconnectRef.current = setTimeout(() => connect(), 3000);
                }
            };
            ws.onerror = () => {
                try { ws.close(); } catch (_) { /* ignore */ }
            };
        } catch (_) {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        if (user && user !== false) {
            connect();
        }
        return () => {
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            if (wsRef.current) {
                try { wsRef.current.close(); } catch (_) { /* ignore */ }
                wsRef.current = null;
            }
        };
    }, [user, connect]);

    return (
        <WebSocketContext.Provider value={{ statuses, connected, discoveryEvent }}>
            {children}
        </WebSocketContext.Provider>
    );
}

export function useWS() {
    return useContext(WebSocketContext) || { statuses: {}, connected: false, discoveryEvent: null };
}
